<template>
  <v-dialog max-width="500px">
    <v-card>
      <v-form ref="formRef" @submit.prevent="submitRole">
        <v-card-title>
          {{
            (roleType === 'referee') ?
                (create ? $t('classes.create_referee') : $t('classes.edit_referee'))
                : (create ? $t('classes.create_coach') : $t('classes.edit_coach'))
          }}
        </v-card-title>

        <v-card-text>
          <v-alert type="error" v-if="errorMessage">{{ errorMessage }}</v-alert>

          <v-select
              v-model="roleData.lvl"
              :items="filteredRoleLvlOptions"
              item-title="text"
              item-value="value"
              :label="$t('classes.choose_class')"
              :rules="[requiredRule]"
              v-if="create"
          ></v-select>
          <v-text-field
              v-model="roleData.valid_from"
              :label="$t('classes.valid_from_dialog')"
              type="date"
              :rules="[requiredRule]"
              v-if="create"
          ></v-text-field>
          <v-text-field
              v-model="roleData.valid_to"
              :label="$t('classes.valid_to_dialog')"
              type="date"
              :rules="validToRules"
          ></v-text-field>
          <v-textarea
              v-model="roleData.description"
              maxlength="50"
              counter
              rows="1"
              auto-grow="true"
              :label="$t('classes.note')"
          ></v-textarea>

          <v-alert
              :text="$t('classes.class_create_info')"
              type="info"
              variant="tonal"
              v-if="create"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn text color="grey" @click="closeDialog">{{ $t('general.cancel') }}</v-btn>
          <v-spacer/>
          <v-btn text color="green" :disabled="isSubmitting" @click="submitRole">
            {{ create ? $t('general.create') : $t('general.save') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>


</template>


<script setup>
import {ref, watch, computed} from 'vue';
import apiClient from '/api.js';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const isDataSet = ref(false); //Prevent race-conditions between watcher and setting the role data
const errorMessage = ref("");
const isSubmitting = ref(false);

const props = defineProps({
  dialog: Boolean,
  roleType: String,
  create: Boolean,
  accountId: Number,
  role: Object,
  excludedRoleLevels: { type: Array, default: () => [] }
});

const emit = defineEmits(['close-dialog', 'role-saved']);

const formRef = ref(null); // Reference na formulář
const requiredRule = (value) => !!value || t('rules.required');
const validToRules = computed(() => {
  // valid_to povinné jen pro referee
  return props.roleType === 'referee' ? [requiredRule] : []
})

const filteredRoleLvlOptions = computed(() => {
  return roleLvlOptions.filter(option => !props.excludedRoleLevels.includes(option.value));
});

const roleLvlOptions = [
  {value: '1', text: t('classes.class_text', { level: 'I.' })},
  {value: '2', text: t('classes.class_text', { level: 'II.' })},
  {value: '3', text: t('classes.class_text', { level: 'III.' })},
];

const roleData = ref({
  lvl: '',
  valid_from: null,
  valid_to: null,
  description: '',
});

// Nastavení prázdných dat
const resetRoleData = () => {
  isDataSet.value = false;
  roleData.value = {
    lvl: '',
    valid_from: null,
    valid_to: null,
    description: '',
  };
};


//Synchronizace při otevření dialogu
watch(() => props.dialog,
    () => {
      errorMessage.value = ""

      if (props.create) {
        resetRoleData(); // Resetujeme pole při vytvoření
        if (props.roleType === 'coach') {
          roleData.value.valid_to = null
        }
      } else if (props.role) {
        roleData.value.lvl = props.role.lvl;
        roleData.value.valid_from = props.role.valid_from
          ? new Date(props.role.valid_from).toISOString().split('T')[0]
          : null

        roleData.value.valid_to = props.role.valid_to
          ? new Date(props.role.valid_to).toISOString().split('T')[0]
          : null

        roleData.value.description = props.role.description;
      }
    }
);

// Sledujeme změny platnosti od a typu role - pouze pro referee
watch(
  () => roleData.value.valid_from,
  (newValidFrom) => {
    if (!props.create || !newValidFrom) return

    // počítat pouze pro REFEREE
    if (props.roleType !== 'referee') {
      roleData.value.valid_to = null
      return
    }

    const yearsToAdd = 5
    const validFromDate = new Date(newValidFrom)
    const validToDate = new Date(validFromDate)
    validToDate.setFullYear(validFromDate.getFullYear() + yearsToAdd)
    validToDate.setDate(validToDate.getDate() - 1)

    roleData.value.valid_to = validToDate.toISOString().split('T')[0]
  }
)

const validateForm = () => {

  // Validace formuláře, jestli jsou vyplněná povinná pole
  if (formRef.value && !formRef.value.validate()) {
    return false;
  }

  return !!props.roleType &&
      !!props.accountId &&
      !!roleData.value.lvl &&
      !!roleData.value.valid_from &&
      (props.roleType === 'coach' ? true : !!roleData.value.valid_to);
};

// Odeslání formuláře
const submitRole = async () => {
  if (validateForm()) {
    try {
      const payload = {
        role_type: props.roleType,
        ...roleData.value,
      };

      // normalizace valid_to
      if (props.roleType === 'coach' && payload.valid_to === '') {
        payload.valid_to = null;
      }

      if (props.create) {
        await apiClient.post(`account/role/?account_id=${props.accountId}`, payload);
      } else {
        await apiClient.put(`account/role/?role_id=${props.role.role_id}`, payload);
      }

      emit('role-saved');
      errorMessage.value = ""
      //  successSnackbar.value = true;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail || 'Unknown error.';

        if (status === 406 || status === 409) {
          errorMessage.value = detail;
        } else {
          console.error(`Neočekávaná chyba: ${status}`, detail);
          errorMessage.value = t('errors.request_failed');
        }
      } else {
        console.error('Chyba při komunikaci s API:', error.message);
        errorMessage.value = t('errors.request_failed');
      }
    }
  }
};

const closeDialog = () => {
  isDataSet.value = false;
  emit('close-dialog')
}

</script>


<style scoped>


</style>